import get from 'get-value'
import { FeaturesConfigDTO } from '../../../NonReact/Types/Types'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Decides whether a branding form tab, sub-area or field is shown, given its optional `featureFlag`.
 *
 * @param featureFlag - The item's `featureFlag` key from `branding-config.yaml` (a `features.yaml` key, dot paths allowed).
 *
 * @returns `true` when the item should be rendered.
 */
export type BrandingFeatureFlagPredicate = (featureFlag?: string) => boolean

/**
 * Returns whether a branding form item with an optional feature flag should be shown.
 *
 * Items without a flag are always shown. Flagged items are shown only once the features config has loaded and the
 * flag is truthy in it, so a flagged item never flashes up while the config is still loading or failed to load.
 *
 * @param featureFlag - The item's `featureFlag` key, or `undefined` when the item is not flagged.
 * @param featuresConfig - The loaded `features.yaml` config, or `null` while loading or after a load failure.
 *
 * @returns `true` when the item should be rendered.
 */
export const isBrandingItemEnabled = (
    featureFlag: Definable<string>,
    featuresConfig: Nullable<FeaturesConfigDTO>
): boolean => {
    if (IsStringEmpty(featureFlag)) return true

    if (featuresConfig === null || featuresConfig === undefined) return false

    return !!get(featuresConfig, featureFlag)
}

/**
 * Builds a {@link BrandingFeatureFlagPredicate} bound to a loaded features config.
 *
 * @param featuresConfig - The loaded `features.yaml` config, or `null` while loading or after a load failure.
 *
 * @returns The predicate.
 */
export const createBrandingFeatureFlagPredicate =
    (featuresConfig: Nullable<FeaturesConfigDTO>): BrandingFeatureFlagPredicate =>
    (featureFlag?: string): boolean =>
        isBrandingItemEnabled(featureFlag, featuresConfig)
